import { Injectable } from '@angular/core';
import {
  MembershipStatus,
  Role,
  UserRolesGQL,
  UserRolesQuery,
} from '@tumi/legacy-app/generated/generated';
import {
  distinctUntilChanged,
  filter,
  map,
  Observable,
  of,
  skipUntil,
  switchMap,
} from 'rxjs';
import { AuthService } from '@auth0/auth0-angular';
import { Price } from '@tumi/legacy-app/utils';
import { onlyCompleteData } from 'apollo-angular';

@Injectable({
  providedIn: 'root',
})
export class PermissionsService {
  constructor(
    private auth: AuthService,
    private userRoles: UserRolesGQL,
  ) {}

  isAdmin() {
    return this.hasRole([Role.Admin]);
  }

  isMember() {
    return this.hasStatus([
      MembershipStatus.Alumni,
      MembershipStatus.Full,
      MembershipStatus.Sponsor,
      MembershipStatus.Trial,
    ]);
  }

  public hasStatus(allowList: MembershipStatus[]): Observable<boolean> {
    return this.auth.isAuthenticated$.pipe(
      skipUntil(this.auth.isLoading$.pipe(filter((loading) => !loading))),
      switchMap((authenticated) => {
        if (!authenticated) {
          return of(false);
        }
        return this.userRoles.watch().valueChanges.pipe(
          onlyCompleteData(),
          map(({ data }) => data.currentUser),
          map((user: UserRolesQuery['currentUser']) => {
            if (!user) {
              return false;
            }
            return allowList.includes(
              user.currentTenant?.status ?? MembershipStatus.None,
            );
          }),
        );
      }),
      distinctUntilChanged(),
    );
  }

  public hasRole(allowList: Role[]): Observable<boolean> {
    return this.auth.isAuthenticated$.pipe(
      skipUntil(this.auth.isLoading$.pipe(filter((loading) => !loading))),
      switchMap((authenticated) => {
        if (!authenticated) {
          return of(false);
        }
        return this.userRoles.watch().valueChanges.pipe(
          onlyCompleteData(),
          map(({ data }) => data.currentUser),
          map((user: UserRolesQuery['currentUser']) => {
            if (!user) {
              return false;
            }
            return allowList.includes(user.currentTenant?.role ?? Role.User);
          }),
        );
      }),
      distinctUntilChanged(),
    );
  }

  getPricesForUser(prices: Price[], eventStartDate: Date): Observable<Price[]> {
    if (!prices) {
      return of([]);
    }
    return this.auth.isAuthenticated$.pipe(
      skipUntil(this.auth.isLoading$.pipe(filter((loading) => !loading))),
      switchMap((authenticated) => {
        if (!authenticated) {
          const defaultPrice = prices.find((price) => price.defaultPrice);
          if (defaultPrice) {
            return of([defaultPrice]);
          }
          return of([]);
        }
        return this.userRoles.watch().valueChanges.pipe(
          onlyCompleteData(),
          map(({ data }) => data.currentUser),
          map((user: UserRolesQuery['currentUser']) => {
            if (!user) {
              return [];
            }
            return prices.filter((price) => {
              if (price.defaultPrice) return true;
              let esnFulfilled;
              if (price.esnCardRequired) {
                esnFulfilled =
                  new Date(user.esnCardValidUntil ?? 0) > eventStartDate;
              } else {
                esnFulfilled = true;
              }
              return (
                esnFulfilled &&
                price.allowedStatusList.includes(
                  user.currentTenant?.status ?? MembershipStatus.None,
                )
              );
            });
          }),
        );
      }),
    );
  }
}
